import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge, MenuItem, surfaceClass, useDismissable } from './ui'
import { orgsApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { initials, titleCase } from '@/lib/format'
import { isPaidPlan } from '@/lib/permissions'
import { queryKeys } from '@/lib/queryClient'
import { useSession } from '@/lib/session'
import type { MembershipSummary } from '@/lib/types'

/**
 * Workspace switcher.
 *
 * Navigation and persistence are separate on purpose. The URL slug is what the
 * app renders — so every screen is linkable and two tabs can sit in two
 * workspaces — while `POST /orgs/{slug}/switch` records the choice server-side
 * so the next sign-in, the iPad and any agent token land in the same place. If
 * that call fails the user still gets where they clicked.
 */
export function OrgSwitcher({ current }: { current: MembershipSummary }) {
  const { organizations } = useSession()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useDismissable(open, close)

  const remember = useMutation({
    mutationFn: (slug: string) => orgsApi.switchTo(slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.session })
    },
  })

  function choose(org: MembershipSummary) {
    setOpen(false)
    if (org.slug !== current.slug) {
      navigate(`/${org.slug}`)
      remember.mutate(org.slug)
    }
  }

  const personal = organizations.filter((org) => org.type === 'personal')
  const teams = organizations.filter((org) => org.type !== 'personal')

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition',
          'hover:bg-ink-100 dark:hover:bg-ink-800',
        )}
      >
        <WorkspaceGlyph name={current.name} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{current.name}</span>
          <span className="block truncate text-xs text-ink-500 dark:text-ink-400">
            {current.type === 'personal' ? 'Personal' : 'Team'} · {current.role}
          </span>
        </span>
        <svg viewBox="0 0 20 20" className="size-4 text-ink-400" aria-hidden="true">
          <path
            d="M6.5 8.5 10 12l3.5-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            surfaceClass,
            'absolute top-full left-0 z-30 mt-1 w-full min-w-64 p-1.5 shadow-lg shadow-ink-950/10',
          )}
        >
          {personal.length > 0 ? (
            <>
              <GroupLabel>Personal</GroupLabel>
              {personal.map((org) => (
                <WorkspaceOption
                  key={org.id}
                  org={org}
                  active={org.slug === current.slug}
                  onSelect={() => choose(org)}
                />
              ))}
            </>
          ) : null}

          {teams.length > 0 ? (
            <>
              <GroupLabel>Teams</GroupLabel>
              {teams.map((org) => (
                <WorkspaceOption
                  key={org.id}
                  org={org}
                  active={org.slug === current.slug}
                  onSelect={() => choose(org)}
                />
              ))}
            </>
          ) : null}

          <div className="my-1.5 border-t border-ink-200 dark:border-ink-800" />
          <MenuItem
            onClick={() => {
              setOpen(false)
              navigate('/new-workspace')
            }}
          >
            <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true">
              <path
                d="M10 5v10M5 10h10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            New workspace
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false)
              navigate('/invitations')
            }}
          >
            <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true">
              <path
                d="M3 6.5h14v9H3zM3 7l7 5 7-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
            Invitations
          </MenuItem>
        </div>
      ) : null}
    </div>
  )
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-medium tracking-wide text-ink-400 uppercase">
      {children}
    </p>
  )
}

function WorkspaceOption({
  org,
  active,
  onSelect,
}: {
  org: MembershipSummary
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left transition',
        active
          ? 'bg-brand-50 dark:bg-brand-950'
          : 'hover:bg-ink-100 dark:hover:bg-ink-800',
      )}
    >
      <WorkspaceGlyph name={org.name} size="sm" />
      <span className="min-w-0 flex-1 truncate text-sm">{org.name}</span>
      {isPaidPlan(org.plan) ? <Badge tone="brand">{titleCase(org.plan)}</Badge> : null}
      {active ? (
        <svg viewBox="0 0 20 20" className="size-4 text-brand-600" aria-hidden="true">
          <path
            d="m5 10.5 3.5 3.5L15 7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </button>
  )
}

/** A workspace's letter tile. Neutral rather than coloured, so the sidebar stays calm. */
export function WorkspaceGlyph({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md bg-ink-200 font-semibold text-ink-700 dark:bg-ink-700 dark:text-ink-100',
        size === 'sm' ? 'size-6 text-[10px]' : 'size-8 text-xs',
      )}
    >
      {initials(name)}
    </span>
  )
}
